import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TokenService } from './token.service';
import { JWTService } from './jwt.service';
import { MixpanelService } from './mixpanel.service';
import {
  AuthSuccessResponse,
  LoginCredentialsDTO,
  UserDTO,
  AuthErrorResponse,
  OAuthCallbackResponse,
  OAuthLoginOptions,
} from '../dtos/auth.dto';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { MixpanelEvent, NomeTela } from '../constants/mixpanel-events.const';
import { RouteMapperUtils } from '../utils/route-mapper.utils';
import { UserRole } from '../enums/user-role.enum';
import { FirebaseMessagingService } from './firebase-messaging.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly API_URL = `${environment.apiUrl}/users`;
  private currentUserSubject = new BehaviorSubject<UserDTO | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  private http = inject(HttpClient);
  private tokenService = inject(TokenService);
  private firebaseService = inject(FirebaseMessagingService);
  private jwtService = inject(JWTService);
  private mixpanelService = inject(MixpanelService);
  private router = inject(Router);
  private routeMapper = inject(RouteMapperUtils);

  constructor() {
    this.loadStoredUser();
  }

  private loadStoredUser(): void {
    const storedUser = this.tokenService.getUser();
    if (storedUser) {
      this.currentUserSubject.next(JSON.parse(storedUser));
    }
  }

  login(
    credentials: LoginCredentialsDTO,
    rememberMe: boolean,
  ): Observable<{
    status: number;
    response: AuthSuccessResponse | AuthErrorResponse;
  }> {
    const params = new HttpParams().set('ignorePrefix', 'true');

    return this.http
      .post<AuthSuccessResponse>(`${this.API_URL}/sessions`, credentials, {
        params,
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<AuthSuccessResponse>) => {
          return {
            status: response.status,
            response: response.body as AuthSuccessResponse | AuthErrorResponse,
          };
        }),
        tap(({ status, response }) => {
          if (status === 200) {
            const successResponse = response as AuthSuccessResponse;

            this.tokenService.setRememberMe(rememberMe);
            this.tokenService.setToken(successResponse.accessToken);
            this.jwtService.setToken(successResponse.accessToken);

            const role = this.jwtService.getRole();

            const user: UserDTO = {
              fullName: successResponse.fullName,
              email: successResponse.email,
              cpf: successResponse.cpf || '',
              birthDate: successResponse.birthDate || '',
              phone: successResponse.phone || '',
              roles: successResponse.roles,
              role: role ?? successResponse.roles[0],
            };

            this.tokenService.setUser(JSON.stringify(user));
            this.currentUserSubject.next(user);

            this.mixpanelService.identify(user.email, {
              fullName: user.fullName,
              email: user.email,
              cpf: user.cpf,
              roles: user.roles,
              loginMethod: 'credentials',
            });

            this.mixpanelService.track(MixpanelEvent.TELA_LOGIN_INICIO_SESSAO, {
              nomeTela: NomeTela.TELA_LOGIN,
              metodoLogin: 'credentials',
              lembrarUsuario: rememberMe,
              usuarioEmail: user.email,
              usuarioNome: user.fullName,
              usuarioCpf: user.cpf,
              usuarioRoles: user.roles,
            });
          } else {
            this.mixpanelService.track(MixpanelEvent.TELA_LOGIN_ERRO, {
              nomeTela: NomeTela.TELA_LOGIN,
              metodoLogin: 'credentials',
              erro: 'Credenciais inválidas',
            });
          }
        }),
        catchError(error => {
          if (error.status === 404) {
            return throwError(
              () =>
                new Error(
                  'O usuário especificado não foi encontrado na base de dados.',
                ),
            );
          }
          if (error.status === 418 || error.status === 401) {
            const errorMessage =
              error.error?.message || 'Credenciais inválidas';
            return throwError(() => new Error(errorMessage));
          }
          return throwError(
            () =>
              new Error(
                'Ocorreu um erro ao tentar fazer login. Por favor, tente novamente.',
              ),
          );
        }),
      );
  }

  async logout(): Promise<void> {
    const currentUser = this.getCurrentUser();
    const nomeTela = this.routeMapper.getNomeTela(this.router.url);
    this.mixpanelService.track(MixpanelEvent.TELA_LOGOUT, {
      nomeTela: nomeTela,
      usuarioEmail: currentUser?.email || null,
      usuarioNome: currentUser?.fullName || null,
    });

    try {
      await this.firebaseService.cleanup();
    } catch (error) {
      console.error('Erro ao limpar notificações no logout:', error);
    }

    this.tokenService.clear();
    this.jwtService.clearTokens();
    this.currentUserSubject.next(null);
    this.mixpanelService.reset();
    this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    return this.tokenService.hasToken();
  }

  getCurrentUser(): UserDTO | null {
    return this.currentUserSubject.value;
  }

  /**
   * Inicia o fluxo de autenticação OAuth com Microsoft
   * @param options Opções de login (popup, redirectUrl)
   * @returns Promise com os dados de autenticação ou void se redirect
   */
  loginWithMicrosoft(
    options: OAuthLoginOptions = {},
  ): Promise<OAuthCallbackResponse> | void {
    if (!environment.oauth?.microsoft?.enabled) {
      throw new Error('Microsoft OAuth não está habilitado neste ambiente');
    }

    const { popup = false, redirectUrl } = options;

    const currentOrigin = window.location.origin;
    const callbackUrl = `${currentOrigin}/auth/callback`;

    let authUrl = environment.oauth.microsoft.authUrl;
    authUrl += `?callbackUrl=${encodeURIComponent(callbackUrl)}`;

    if (redirectUrl) {
      authUrl += `&redirect=${encodeURIComponent(redirectUrl)}`;
    }

    if (popup) {
      return this.openOAuthPopup(authUrl);
    }

    window.location.href = authUrl;
  }

  /**
   * Abre popup para OAuth e aguarda resposta
   */
  private openOAuthPopup(authUrl: string): Promise<OAuthCallbackResponse> {
    return new Promise((resolve, reject) => {
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        'Microsoft Login',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`,
      );

      if (!popup) {
        reject(new Error('Não foi possível abrir a janela de login'));
        return;
      }

      const messageHandler = (event: MessageEvent) => {
        const allowedOrigins = [environment.apiUrl, window.location.origin];

        if (!allowedOrigins.includes(event.origin)) {
          return;
        }

        if (event.data.type === 'AUTH_SUCCESS') {
          window.removeEventListener('message', messageHandler);
          popup.close();

          const response: OAuthCallbackResponse = event.data.data;
          this.handleOAuthSuccess(response);
          resolve(response);
        }

        if (event.data.type === 'AUTH_ERROR') {
          window.removeEventListener('message', messageHandler);
          popup.close();
          reject(new Error(event.data.error || 'Erro na autenticação'));
        }
      };

      window.addEventListener('message', messageHandler);

      const checkClosed = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          reject(new Error('Login cancelado pelo usuário'));
        }
      }, 1000);
    });
  }

  /**
   * Processa callback OAuth (chamado pela página de callback)
   */
  handleOAuthCallback(response: OAuthCallbackResponse): void {
    this.handleOAuthSuccess(response);

    if (window.opener) {
      window.opener.postMessage(
        {
          type: 'AUTH_SUCCESS',
          data: response,
        },
        window.location.origin,
      );
      window.close();
      return;
    }

    const defaultRoute = this.getDefaultRouteByRole();
    this.router.navigate([defaultRoute]);
  }

  /**
   * Processa sucesso do OAuth e salva dados
   */
  private handleOAuthSuccess(response: OAuthCallbackResponse): void {
    this.tokenService.setRememberMe(true);
    this.tokenService.setToken(response.access_token);
    this.jwtService.setToken(response.access_token);

    const role = this.jwtService.getRole();

    const user: UserDTO = {
      fullName: response.user.name,
      email: response.user.email,
      cpf: '',
      birthDate: '',
      phone: '',
      roles: [],
      role: role ?? '',
    };

    this.tokenService.setUser(JSON.stringify(user));
    this.currentUserSubject.next(user);

    this.mixpanelService.identify(user.email, {
      fullName: user.fullName,
      email: user.email,
      loginMethod: 'oauth_microsoft',
      isNewUser: response.isNewUser,
    });

    this.mixpanelService.track(MixpanelEvent.TELA_LOGIN_INICIO_SESSAO, {
      nomeTela: NomeTela.CALLBACK_AUTENTICACAO,
      metodoLogin: 'oauth_microsoft',
      usuarioNovo: response.isNewUser,
      usuarioEmail: user.email,
      usuarioNome: user.fullName,
    });
  }

  /**
   * Verifica se OAuth Microsoft está disponível
   */
  isMicrosoftOAuthEnabled(): boolean {
    return environment.oauth?.microsoft?.enabled ?? false;
  }

  getDefaultRouteByRole(): string {
    const role = this.jwtService.getRole();

    switch (role) {
      case UserRole.SALES_OPS:
        return '/proposta/avaliacao';
      case UserRole.SALES_REP:
        return '/proposta';
      case UserRole.ADMIN:
        return '/';
      case UserRole.FIELD_SALES:
        return '/';
      default:
        return '/';
    }
  }
}
